// src/components/brand/brand.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "../../core/pagination/datatable";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonFooter from "../../core/common/footer/commonFooter";
import { PlusCircle, X } from "feather-icons-react/build/IconComponents";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  createBrand,
  getAllBrands,
  updateBrandAPI,
  deleteBrandAPI,
} from "../../api/brandApi";
import { uploadCatalogImage } from "../../api/productApi";
import { toast } from "react-toastify";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

const DEFAULT_BRAND_IMAGE = "/assets/img/by-default.png";

// ---------- helpers: same convention as subcategory.jsx ----------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/"); // fix legacy
  if (!/^catalog\//i.test(v)) {
    v = `catalog/${v.split("/").pop()}`;
  }
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already URL
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};
// ---------------------------------------------------------------

const BrandList = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteBrands =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "brands");

  // create form state
  const [brandNameEN, setBrandNameEN] = useState("");
  const [brandNameAR, setBrandNameAR] = useState("");
  const [status, setStatus] = useState(true);

  // create modal image state
  const [logoKey, setLogoKey] = useState(""); // "catalog/..."
  const [uploadPreview, setUploadPreview] = useState(null); // for immediate UX

  // list + selection
  const [brandList, setBrandList] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // edit modal state
  const [editBrandId, setEditBrandId] = useState(null);
  const [editBrandNameEN, setEditBrandNameEN] = useState("");
  const [editBrandNameAR, setEditBrandNameAR] = useState("");
  const [editStatus, setEditStatus] = useState(true);

  const [editLogoKey, setEditLogoKey] = useState(""); // "catalog/..."
  const [editLogoPreview, setEditLogoPreview] = useState(""); // S3 URL or local data URL

  // misc
  const [deleteBrandId, setDeleteBrandId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 🔄 Fetch brand list
  const fetchBrands = async () => {
    try {
      const data = await getAllBrands();
      setBrandList(data || []);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to load brands")}`);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // when an item is picked for edit, populate the edit modal state
  useEffect(() => {
    if (!selectedBrand) return;

    const id = selectedBrand._id || selectedBrand.id;
    setEditBrandId(id || null);

    const en = selectedBrand.brandName?.en || selectedBrand.brand || "";
    const ar = selectedBrand.brandName?.ar || "";
    setEditBrandNameEN(en);
    setEditBrandNameAR(ar);

    const k = normalizeKey(selectedBrand.logo || "");
    setEditLogoKey(k);
    setEditLogoPreview(toPublicUrl(k));

    setEditStatus(
      selectedBrand.status === "Active" ||
        selectedBrand.status === true ||
        selectedBrand.status === "active"
    );
  }, [selectedBrand]);

  // CREATE modal: upload to S3 and store key
  const handleCreateImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const dataUrl = reader.result;

        // show local preview fast
        setUploadPreview(dataUrl);

        // deterministic name
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date()
          .toISOString()
          .replace(/[-:.TZ]/g, "")
          .slice(0, 14);
        const base = (brandNameEN || "brand")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `BRAND_${base || "brand"}_${stamp}.${ext}`;

        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        const k = normalizeKey(key);
        setLogoKey(k);
        // switch preview to S3 url
        setUploadPreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setLogoKey("");
        setUploadPreview(null);
      }
    };
    reader.readAsDataURL(file);
  };

  // EDIT modal: upload to S3 and store key
  const handleEditImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const dataUrl = reader.result;

        // quick local preview
        setEditLogoPreview(dataUrl);

        // deterministic name
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date()
          .toISOString()
          .replace(/[-:.TZ]/g, "")
          .slice(0, 14);
        const base = (editBrandNameEN || "brand")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `BRAND_${base || "brand"}_${stamp}.${ext}`;

        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        const k = normalizeKey(key);
        setEditLogoKey(k);
        setEditLogoPreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setEditLogoKey("");
        setEditLogoPreview("");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canWriteBrands) {
      toast.error(`❌ ${window.translate("You don't have permission to modify brands")}`);
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    const normalize = (str) =>
      (str || "").toLowerCase().replace(/\s+/g, "").trim();

    if (!brandNameEN.trim()) {
      toast.warn("⚠️ Brand name (EN) is required.");
      setSubmitting(false);
      return;
    }

    const duplicate = brandList.find(
      (item) => normalize(item.brandName?.en) === normalize(brandNameEN)
    );
    if (duplicate) {
      toast.warn("❌ Brand with this English name already exists in the list.");
      setSubmitting(false);
      return;
    }

    if (!logoKey) {
      toast.warn(window.translate("Please upload a brand logo."));
      setSubmitting(false);
      return;
    }

    const payload = {
      brandName: {
        en: brandNameEN,
        ar: brandNameAR,
      },
      logo: normalizeKey(logoKey), // store KEY ONLY
      status,
    };

    try {
      await createBrand(payload);
      toast.success(` ${window.translate("Brand created successfully!")}`);
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      setSubmitting(false);
      if (error.response?.status === 409) {
        toast.error(
          `❌ ${window.translate("Brand with this name already exists.")}`
        );
      } else {
        toast.error(error.message || window.translate("Failed to create brand"));
      }
    }
  };

  const handleEditSubmit = async () => {
    if (!canWriteBrands) {
      toast.error(`❌ ${window.translate("You don't have permission to modify brands")}`);
      return;
    }
    try {
      const payload = {
        brandName: {
          en: editBrandNameEN,
          ar: editBrandNameAR,
        },
        logo: normalizeKey(editLogoKey), // store KEY ONLY
        status: editStatus,
      };

      await updateBrandAPI(editBrandId, payload);
      toast.success(` ${window.translate("Brand updated successfully!")}`);

      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      const friendly = window.translate("Failed to update brand");
      toast.error(`⚠️ ${friendly}`);
    }
  };

  const handleDeleteBrand = async () => {
    const t = (s) => (window?.translate ? window.translate(s) : s);
    if (!canWriteBrands) {
      toast.error(`❌ ${t("You don't have permission to modify brands")}`);
      return;
    }
    try {
      if (!deleteBrandId) return;
      await deleteBrandAPI(deleteBrandId);
      toast.success(` ${t("Brand deleted successfully!")}`);
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      toast.error(`${t("Failed to delete brand")}`);
    }
  };

  const columns = [
    {
      title: window.translate("Brand"),
      dataIndex: "brand",
      render: (brand) => brand || "Unnamed",
      sorter: (a, b) => (a.brand || "").localeCompare(b.brand || ""),
    },
    {
      title: window.translate("Image"),
      dataIndex: "logo",
      render: (text, record) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <img
              alt="brand"
              src={record.logo ? toPublicUrl(record.logo) : DEFAULT_BRAND_IMAGE}
              style={{ width: "64px", height: "64px", objectFit: "contain" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DEFAULT_BRAND_IMAGE;
              }}
            />
          </Link>
        </span>
      ),
      sorter: (a, b) => String(a.logo || "").localeCompare(String(b.logo || "")),
      width: "7%",
    },
    {
      title: window.translate("Created Date"),
      dataIndex: "createdon",
      sorter: (a, b) => (a.createdon || "").localeCompare(b.createdon || ""),
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge table-badge fw-medium fs-10 ${
            text === "Active" || text === true ? "bg-success" : "bg-danger"
          }`}
        >
          {text === true ? "Active" : text === false ? "Inactive" : text}
        </span>
      ),
      sorter: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || "")),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) =>
        canWriteBrands ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-brand"
                onClick={() => setSelectedBrand(record.original)}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => setDeleteBrandId(record.id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("brand")}</h4>
                <h6>{window.translate("manageBrands")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteBrands && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-brand"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i>
                  {window.translate("addBrand")}
                </Link>
              </div>
            )}
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("sortLast7Days")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("recentlyAdded")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("ascending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("descending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("lastMonth")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("last7Days")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive brand-table">
                {brandList.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={brandList.map((b) => {
                      const brandName =
                        typeof b.brandName === "object"
                          ? b.brandName
                          : {
                              en: b.brandName?.en || b.brand || "",
                              ar: b.brandName?.ar || "",
                            };

                      const currentLang = localStorage.getItem("lang") || "en";
                      const createdDate = b.createdAt || b.createdon;

                      return {
                        key: b._id || b.id,
                        id: b._id || b.id,
                        brand:
                          brandName[currentLang] || brandName.en || "Unnamed",
                        logo: b.logo, // key or url; renderer uses toPublicUrl
                        createdon: createdDate
                          ? new Date(createdDate).toLocaleDateString()
                          : "N/A",
                        status: b.status,
                        original: {
                          ...b,
                          brandName,
                        },
                      };
                    })}
                  />
                ) : (
                  <p className="text-muted text-center my-4">
                    {window.translate("Loading brands or no data available")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>

      <>
        {/* Add Brand */}
        <div className="modal fade" id="add-brand">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("addBrand")}</h4>
                    </div>
                    <button
                      type="button"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>

                  <div className="modal-body custom-modal-body new-employee-field">
                    <form onSubmit={handleSubmit}>
                      <div className="profile-pic-upload mb-3 gap-2">
                        <div className="profile-pic brand-pic">
                          {uploadPreview ? (
                            <img
                              src={uploadPreview}
                              alt="preview"
                              style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover" }}
                            />
                          ) : (
                            <span>
                              <PlusCircle className="plus-down-add" />
                              {window.translate("addImage")}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleCreateImageChange}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("uploadImage")}</h4>
                            </div>
                          </div>
                          <p className="mt-2">{window.translate("imageNote")}</p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("brand")} EN
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={brandNameEN}
                          onChange={(e) => setBrandNameEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("brand")} AR
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={brandNameAR}
                          onChange={(e) => setBrandNameAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="brand-status"
                            className="check"
                            checked={status}
                            onChange={(e) => setStatus(e.target.checked)}
                          />
                          <label htmlFor="brand-status" className="checktoggle" />
                        </div>
                      </div>

                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                          data-bs-dismiss="modal"
                        >
                          {window.translate("cancel")}
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary fs-13 fw-medium p-2 px-3"
                        >
                          {submitting
                            ? window.translate("processing")
                            : window.translate("addBrand")}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Add Brand */}

        {/* Edit Brand */}
        <div className="modal fade" id="edit-brand">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("editBrand")}</h4>
                    </div>
                    <button
                      type="button"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>

                  <div className="modal-body custom-modal-body new-employee-field">
                    <form>
                      <div className="profile-pic-upload mb-3 gap-2">
                        <div className="profile-pic brand-pic position-relative">
                          {editLogoPreview ? (
                            <img
                              src={editLogoPreview}
                              alt="Edit Brand"
                              style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover" }}
                            />
                          ) : (
                            <span>
                              <PlusCircle className="plus-down-add" />
                              {window.translate("addImage")}
                            </span>
                          )}

                          {/* Optional: remove button */}
                          {editLogoKey ? (
                            <Link
                              to="#"
                              className="remove-photo"
                              title={window.translate("remove")}
                              onClick={(e) => {
                                e.preventDefault();
                                setEditLogoKey("");
                                setEditLogoPreview("");
                              }}
                            >
                              <X className="x-square-add" />
                            </Link>
                          ) : null}
                        </div>

                        <div>
                          <div className="image-upload mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleEditImageUpload}
                            />
                            <div className="image-uploads">
                              <h4>{window.translate("changeImage")}</h4>
                            </div>
                          </div>
                          <p className="mt-2">{window.translate("imageNote")}</p>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("brand")} EN
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editBrandNameEN}
                          onChange={(e) => setEditBrandNameEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("brand")} AR
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editBrandNameAR}
                          onChange={(e) => setEditBrandNameAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user4"
                            className="check"
                            checked={editStatus}
                            onChange={() => setEditStatus(!editStatus)}
                          />
                          <label htmlFor="user4" className="checktoggle" />
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <button
                      onClick={handleEditSubmit}
                      data-bs-dismiss="modal"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                    >
                      {window.translate("saveChanges")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Edit Brand */}

        <CommonDeleteModal onConfirm={handleDeleteBrand} />
      </>
    </div>
  );
};

export default BrandList;
